/**
 * useCyclePipelineStatus — optimistic click-to-cycle or arbitrary-set pipeline
 * status mutation.
 *
 * conversation-model-versioning Phase 5 Session 8 (ADR 0020).
 * `targetStatus` additive extension: DEAL STATUS dropdown (2026-05-18).
 *
 * Mirrors `useRevokeShare` (`use-shares.ts`) — optimistic cache flip + whole-
 * snapshot rollback on error + `onSettled` invalidation. Both surfaces (cockpit
 * `<PipelineSummary>` + Pipeline-page `<DealTable>`) import this SAME hook
 * (ONE write path, ADR 0020 §Consequences).
 *
 * Behavioural contract (ADR 0020 VERBATIM):
 *   onMutate  → cancelQueries(all), snapshot BOTH list() + detail(id) as whole
 *               objects (race-safe restore), resolve status = targetStatus ??
 *               nextPipelineStatus(current), setQueryData to patch BOTH caches
 *               with the SAME resolved status, return context.
 *   onError   → restore BOTH caches from whole-object snapshots (no field
 *               surgery — race-safe per ADR 0020 §Alternatives considered).
 *   onSettled → invalidateQueries(all) — single sweep covers list + detail +
 *               diffs + modelView + re-pulls ADR-0018 displayVersion.
 *
 * `targetStatus` (optional, additive): when supplied, the hook sends THAT
 * status to the server and uses it for the optimistic patch — bypasses the
 * nextPipelineStatus cycle. Default (omit `targetStatus`) behaviour is
 * IDENTICAL to the pre-extension hook — all existing call sites are back-compat.
 *
 * `Passed` semantics: `nextPipelineStatus('Passed') === 'Passed'` (no-op by
 * the P1-shipped function). A stray cycle-click on a Passed deal is
 * structurally inert — the hook issues a PUT with `status: 'Passed'` and the
 * server writes the same value. No special-casing required here.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { nextPipelineStatus } from '@arcanum-hq/shared-types';
import type { PipelineStatus } from '@arcanum-hq/shared-types';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { ConversationItem } from './use-conversations-v2';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MutationVars {
  conversationId: string;
  currentStatus: PipelineStatus;
  /**
   * Optional explicit target status. When supplied, the PUT body and optimistic
   * patch both use this value — bypasses the nextPipelineStatus() cycle.
   * Default (omit) = cycle behaviour (back-compat with all existing call sites).
   */
  targetStatus?: PipelineStatus;
}

interface RollbackContext {
  /** Whole-object snapshot of queryKeys.conversations.list() — race-safe restore (ADR 0020). */
  prevList: { data: ConversationItem[]; total: number } | undefined;
  /** Whole-object snapshot of queryKeys.conversations.detail(id) — race-safe restore (ADR 0020). */
  prevDetail: unknown;
}

// PUT /api/conversations/:id/status returns HTTP 204 (no content).
// The optimistic onMutate/onSettled path never reads the resolved value,
// so the mutation generic is void — matches the actual wire contract (F04).

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Click-to-cycle the `pipelineStatus` of a conversation.
 *
 * Both the Cockpit `<PipelineSummary>` and the Pipeline `<DealTable>` import
 * this hook — it is surface-agnostic (takes only the conversation id + current
 * status; no surface-specific coupling).
 *
 * @example
 * ```ts
 * const { mutate: cycle } = useCyclePipelineStatus();
 * cycle({ conversationId: deal.id, currentStatus: deal.pipelineStatus });
 * ```
 */
export function useCyclePipelineStatus() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, MutationVars, RollbackContext>({
    mutationFn: ({ conversationId, currentStatus, targetStatus }) => {
      // Resolve the status to send: explicit target takes priority over cycle.
      const status = targetStatus ?? nextPipelineStatus(currentStatus);
      return api.put<void>(`/api/conversations/${conversationId}/status`, {
        status,
      });
    },

    onMutate: async ({ conversationId, currentStatus, targetStatus }) => {
      // Cancel any in-flight refetches under conversations.all so they don't
      // overwrite our optimistic patch when they resolve (ADR 0020 §Decision 1).
      await queryClient.cancelQueries({
        queryKey: queryKeys.conversations.all,
      });

      // Snapshot BOTH caches as whole objects — race-safe restore (ADR 0020
      // §Decision 1; §Alternatives considered: field-surgery is NOT race-safe).
      const prevList = queryClient.getQueryData<{
        data: ConversationItem[];
        total: number;
      }>(queryKeys.conversations.list());

      const prevDetail = queryClient.getQueryData(
        queryKeys.conversations.detail(conversationId)
      );

      // Resolve the SAME status as mutationFn — optimistic patch MUST match
      // the actual wire value (explicit target takes priority over cycle).
      const resolvedStatus = targetStatus ?? nextPipelineStatus(currentStatus);

      // Patch list() cache — map the matching conversation's pipelineStatus.
      if (prevList) {
        queryClient.setQueryData<{ data: ConversationItem[]; total: number }>(
          queryKeys.conversations.list(),
          {
            ...prevList,
            data: prevList.data.map((conv) =>
              conv.id === conversationId
                ? { ...conv, pipelineStatus: resolvedStatus }
                : conv
            ),
          }
        );
      }

      // Patch detail() cache — set the status field. The detail cache shape
      // mirrors ApiConversationDetailResponse.data (use-conversations-v2.ts).
      if (prevDetail) {
        queryClient.setQueryData(
          queryKeys.conversations.detail(conversationId),
          (old: unknown) => {
            if (!old || typeof old !== 'object') return old;
            const cast = old as { data?: { status?: string } };
            if (!cast.data) return old;
            return {
              ...cast,
              data: { ...cast.data, status: resolvedStatus },
            };
          }
        );
      }

      return { prevList, prevDetail };
    },

    onError: (_err, { conversationId }, ctx) => {
      // Whole-object restore — no field merge, no partial reconciliation
      // (ADR 0020 §Decision 2; §Alternatives considered).
      if (ctx?.prevList !== undefined) {
        queryClient.setQueryData(queryKeys.conversations.list(), ctx.prevList);
      }
      if (ctx?.prevDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.conversations.detail(conversationId),
          ctx.prevDetail
        );
      }
    },

    onSettled: () => {
      // Single invalidation covers list + detail + diffs + modelView + re-pulls
      // ADR-0018 displayVersion (ADR 0020 §Decision 3).
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.all,
      });
    },
  });
}
